import { api } from '@/lib/api/client';
import { isDataErrors } from '@/lib/api/types';
import type { DataError, ServerResponse } from '@/lib/api/types';
import { readAccess } from '@/types/flightAccess';
import type { FlightAccess, FlightAccessCard } from '@/types/flightAccess';
import type {
  UserEditCard,
  UserInfo,
  UserProfileCard,
  UserRegisterCard,
} from '@/types/user';

/**
 * The user service.
 *
 * Everything here except the two `/user/me` calls is **ADMIN only** — `SecurityConfig`
 * matches `/user/me` before `/user/**`, first match wins, and that ordering is the
 * only reason self-service is reachable at all. Any other path answers 403 for
 * every other role.
 *
 * There is **no delete**. A user's card and everything they have done reference the
 * row, so it stays; `setUserActivity(id, false)` is what "delete" means here, and a
 * disabled account simply cannot sign in.
 *
 * `password` is never returned by any of these. It is a bcrypt hash, it used to
 * appear in every response, and it is gone — nothing here reads or stores one.
 */

/**
 * The outcome of a write, as a union so the check cannot be skipped.
 *
 * This backend answers a REJECTED write with **HTTP 200** and `successfull: false`
 * (its own spelling), so `response.ok` is true for a write that did not happen.
 * Branching on the status code silently reports failures as successes — which is
 * why nothing outside this file ever sees the envelope.
 */
export type UserWriteResult =
  | { ok: true; user: UserInfo }
  | {
      ok: false;
      /** One per rejected field, each carrying the `path` to attach it to. */
      errors: DataError[];
      /** The rejection that is not about a field — a username already taken. */
      message: string | null;
    };

/**
 * Reads the envelope, once.
 *
 * `payload` is the saved `UserInfo` on success and a `DataError[]` on a validation
 * failure — two shapes in one field. The array check is what separates them, so a
 * `successfull: true` carrying the wrong shape lands on the failure branch instead
 * of being handed onward as a user.
 */
const settle = (response: ServerResponse<UserInfo | DataError[]>): UserWriteResult => {
  const { payload } = response;

  if (response.successfull && payload !== null && !Array.isArray(payload)) {
    return { ok: true, user: payload };
  }

  return {
    ok: false,
    errors: isDataErrors(payload) ? payload : [],
    message: response.errorMessage,
  };
};

/** Every user. No paging and no filter — this endpoint returns the lot. */
export const fetchUsers = (signal?: AbortSignal): Promise<UserInfo[]> =>
  api
    .get<UserInfo[]>('/user/all', undefined, signal)
    .then((response) => (Array.isArray(response) ? response : []));

/**
 * One user.
 *
 * **A missing id is `200 null`, not a 404** — the same trap as the flight endpoint —
 * so "the request worked" does not mean "the user exists".
 */
export const fetchUser = (id: number, signal?: AbortSignal): Promise<UserInfo | null> =>
  api.get<UserInfo | null>(`/user/id/${id}`, undefined, signal);

/**
 * Creates a user in a company.
 *
 * The company is a QUERY parameter rather than a field of the body, and it is
 * required: a user exists inside a company here, and the role has to be one that
 * company's type allows or the write is rejected. That allowed-role mapping lives on
 * the server and is not published, so this app does not try to predict it — it sends
 * the chosen role and shows what comes back on the role field.
 */
export const createUser = (companyId: number, card: UserRegisterCard): Promise<UserWriteResult> =>
  api
    .post<ServerResponse<UserInfo | DataError[]>>(`/user/add${buildCompany(companyId)}`, card)
    .then(settle);

const buildCompany = (companyId: number): string => `?companyId=${encodeURIComponent(companyId)}`;

/**
 * Edits any user, as an administrator.
 *
 * **Partial.** Only the fields present in `card` are applied, and sending `null` for
 * one does NOT clear it — so a caller builds the body from what actually changed
 * rather than posting the whole object back.
 *
 * Two things the server refuses, both of them the same rule: an administrator cannot
 * take the ADMIN role from themselves, and cannot disable their own account. Roles
 * are read from the database on every request, so either would take effect on the
 * next call with no way back in through this API. Another administrator can still do
 * both.
 */
export const editUser = (id: number, card: UserEditCard): Promise<UserWriteResult> =>
  api.post<ServerResponse<UserInfo | DataError[]>>(`/user/edit/id/${id}`, card).then(settle);

/**
 * Enables or disables a user — the delete of this resource.
 *
 * The flag is a query parameter and the request has no body.
 */
export const setUserActivity = (id: number, isActive: boolean): Promise<UserWriteResult> =>
  api
    .post<ServerResponse<UserInfo | DataError[]>>(
      `/user/activity/id/${id}?isActive=${isActive ? 'true' : 'false'}`,
    )
    .then(settle);

/**
 * The caller's own account. Any signed-in role.
 *
 * No id: the account comes from the token, which is what makes this endpoint
 * impossible to point at somebody else.
 */
export const fetchMe = (signal?: AbortSignal): Promise<UserInfo | null> =>
  api.get<UserInfo | null>('/user/me', undefined, signal);

/**
 * Edits the caller's own account. Any signed-in role.
 *
 * Takes a `UserProfileCard`, which is deliberately not a `UserEditCard`: it has no
 * `role`, no `isActive` and no `username`, so this endpoint cannot be used to raise
 * a privilege. Do not build an admin form on top of it, or a profile form on top of
 * `editUser` — the two shapes are the enforcement.
 */
export const editMe = (card: UserProfileCard): Promise<UserWriteResult> =>
  api.post<ServerResponse<UserInfo | DataError[]>>('/user/me', card).then(settle);

/*
  Flight access — which flights a user sees. ADMIN only, like the rest of `/user`.

  The caller's OWN access is `GET /user/me/flight-access`, and it lives in
  `lib/api/flightAccess.ts`: the app shell reads it for every role, and a feature may
  not import from another feature.
*/

/** The outcome of an access write, as a union so the check cannot be skipped. */
export type FlightAccessWriteResult =
  | { ok: true; access: FlightAccess }
  | {
      ok: false;
      /** One per rejected field — `restricted`, `airports` or `airlines`. */
      errors: DataError[];
      /** The rejection that is not about a field — an unknown user. */
      message: string | null;
    };

/**
 * Reads an access envelope, once.
 *
 * Same two-shapes-in-one-field envelope as the user writes: `payload` is the saved
 * `FlightAccess` on success and a `DataError[]` on a validation failure, and the
 * status code is 200 either way.
 */
const settleAccess =
  (userId: number) =>
  (response: ServerResponse<unknown>): FlightAccessWriteResult => {
    const { payload } = response;

    if (response.successfull && payload !== null && typeof payload === 'object' && !Array.isArray(payload)) {
      return { ok: true, access: readAccess(payload, userId) };
    }

    return {
      ok: false,
      errors: isDataErrors(payload) ? payload : [],
      message: response.errorMessage,
    };
  };

/**
 * Every SAVED access, sorted by user.
 *
 * **Only users somebody has set an access for are in it.** A user who is not sees
 * every flight — that includes every account that existed before this feature — so a
 * missing entry is an answer, not a gap. The list screen joins this to `/user/all` on
 * the user id.
 */
export const fetchFlightAccesses = (signal?: AbortSignal): Promise<FlightAccess[]> =>
  api
    .get<unknown>('/user/flight-access/all', undefined, signal)
    .then((response) =>
      Array.isArray(response)
        ? response.flatMap((entry: unknown) => {
            const userId = (entry as { userId?: unknown } | null)?.userId;
            // An entry that does not say whose it is cannot be joined to anybody.
            return typeof userId === 'number' ? [readAccess(entry, userId)] : [];
          })
        : [],
    );

/**
 * Creates or replaces one user's access — a user has exactly one, so the two are the
 * same call.
 *
 * **The body replaces the access whole**: the lists are the complete selection. To
 * give the user every flight again, send `{ restricted: false }`; the server clears
 * the lists and records who did it, which is why this, and not the delete below, is
 * what the screen uses for that.
 *
 * Refused for an ADMIN user — an administrator always sees every flight.
 */
export const saveFlightAccess = (
  userId: number,
  card: FlightAccessCard,
): Promise<FlightAccessWriteResult> =>
  api
    .post<ServerResponse<unknown>>(`/user/flight-access/id/${userId}`, card)
    .then(settleAccess(userId));

/**
 * Deletes the saved access, so the user is back on the default — every flight — with
 * no record of who did it or when.
 *
 * A clean-up, not the everyday "give them every flight": the screen offers it for one
 * case only, a restriction still stored for an account that has since become an
 * administrator, where a save is refused and the row would otherwise sit there
 * waiting to apply again if the role is ever taken away.
 */
export const deleteFlightAccess = (userId: number): Promise<FlightAccessWriteResult> =>
  api
    .post<ServerResponse<unknown>>(`/user/flight-access/delete/id/${userId}`)
    .then(settleAccess(userId));
